import * as Sentry from "@sentry/react";
import { commsApi, type PushSubscriptionPayload } from "@/lib/api/comms";

/** True when this browser can register a service worker AND do web push.
 *  Safari on older iOS, private windows, and some embedded webviews miss one
 *  or both — callers use this to disable the web-push control gracefully. */
export function isPushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Convert a base64url-encoded VAPID public key into the ``Uint8Array`` the
 *  Push API's ``applicationServerKey`` expects. */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(new ArrayBuffer(rawData.length));
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** Register (or reuse) the portal service worker. Returns null when the
 *  browser can't do web push at all, so callers can no-op cleanly. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;
  return navigator.serviceWorker.register("/sw.js");
}

/** Serialise a ``PushSubscription`` into the backend's wire shape. */
function serializeSubscription(sub: PushSubscription): PushSubscriptionPayload | null {
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return null;
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  };
}

/** Full subscribe flow: register the SW, ask permission, fetch the VAPID key,
 *  subscribe via the Push API, and persist the subscription server-side.
 *  Returns true on success. Throws with a human-readable message on failure
 *  (unsupported browser, denied permission, network error) so the caller can
 *  toast it. */
export async function subscribeToPush(): Promise<boolean> {
  if (!isPushSupported()) {
    throw new Error("This browser doesn't support notifications.");
  }

  const registration = await registerServiceWorker();
  if (!registration) {
    throw new Error("This browser doesn't support notifications.");
  }
  // Ensure the worker is active before subscribing.
  await navigator.serviceWorker.ready;

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was not granted.");
  }

  const { data } = await commsApi.getVapidKey();
  if (!data.public_key) {
    throw new Error("Push notifications aren't configured on the server.");
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.public_key),
  });

  const payload = serializeSubscription(subscription);
  if (!payload) {
    throw new Error("Could not read the push subscription.");
  }

  await commsApi.savePushSubscription(payload);
  return true;
}

/** Tear down the current push subscription (browser + server). Best-effort:
 *  a missing subscription is treated as already-unsubscribed. */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isPushSupported()) return false;

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return false;

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return false;

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();

  try {
    await commsApi.deletePushSubscription(endpoint);
  } catch (err) {
    // The local subscription is already gone; a failed server delete just
    // leaves a stale row the backend prunes on next push. Don't fail the UX.
    Sentry.captureException(err, { tags: { kind: "push-unsubscribe" } });
  }
  return true;
}

/** Whether this browser has a push subscription right now. */
export async function hasPushSubscription(): Promise<boolean> {
  if (!isPushSupported()) return false;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return false;
  return (await registration.pushManager.getSubscription()) !== null;
}
